// Story beats — the authored narrative layer. Pure data + builders: NO store,
// NO EventBus, NO game imports (the StorySystem wires these builders to engine
// events). Each beat is presented through one of two channels:
//   - 'modal': a cinematic full-screen reveal (StoryBeatModal)
//   - 'feed' : a social-feed post by one of the fictional FEED_AUTHORS
// Beats are region/club-aware and de-duplicated by id via seenStoryBeats, so a
// given beat fires once per playthrough. Beats carry NO economic effects in this
// phase, which keeps the deterministic tests untouched.

import type { RegionId } from './regionConfig'
import { REGION_META } from './regionConfig'
import type { GameMode } from './gameModesConfig'

export type StoryPresentation = 'modal' | 'feed' | 'choice'

export interface StoryModal {
  kicker: string
  title: string
  body: string
  heroImage?: string
  gradient?: [string, string]
  /** Button label (defaults to "Continuar") */
  cta?: string
}

export interface StoryFeed {
  /** must match a FEED_AUTHORS id (e.g. 'rival-dt', 'padel-press', 'fan-marta') */
  authorId: string
  headline: string
}

export interface StoryChoice {
  /** an EventType registered in eventsConfig (rendered by EventPopup) */
  eventType: string
}

export interface StoryBeat {
  id: string
  presentation: StoryPresentation
  modal?: StoryModal
  feed?: StoryFeed
  choice?: StoryChoice
}

export interface StoryContext {
  region: RegionId
  clubName: string
  gameMode: GameMode
  day: number
  /** raw EventBus payload for event-driven beats (shape depends on the event) */
  payload?: unknown
}

/** Small helper for the common feed-post beat */
function feedBeat(id: string, authorId: string, headline: string): StoryBeat {
  return { id, presentation: 'feed', feed: { authorId, headline } }
}

// Structural shapes of the EventBus payloads we read (kept local so this config
// stays pure — no dependency on the game layer / EventBus).
interface SeasonOutcome {
  season: number
  divisionName: string
  position: number
  promoted: boolean
  relegated: boolean
  title: boolean
}
interface ObjectiveOutcome {
  season: number
  divisionName: string
  objectiveLabel: string
}

// ---------------------------------------------------------------------------
// Opening arc — seeded once at new-game (never on "Continuar")
// ---------------------------------------------------------------------------

/** The lore hook shown right after a new game (migrated from RegionIntroModal). */
export function gameStartBeat(ctx: StoryContext): StoryBeat {
  const meta = REGION_META[ctx.region]
  return {
    id: `gameStart:${ctx.region}`,
    presentation: 'modal',
    modal: {
      kicker: 'Tu nuevo hogar',
      title: meta.label,
      body: meta.intro,
      heroImage: meta.heroImage,
      gradient: meta.gradient,
      cta: 'Empezar',
    },
  }
}

/** Local press notices the new club (region + club aware). */
export function welcomePressBeat(ctx: StoryContext): StoryBeat {
  const meta = REGION_META[ctx.region]
  return {
    id: `welcome:${ctx.region}`,
    presentation: 'feed',
    feed: {
      authorId: 'padel-press',
      headline: `${ctx.clubName} abre sus puertas en ${meta.label}. Un nuevo proyecto de pádel echa a andar.`,
    },
  }
}

/** The recurring rival's first, condescending jab (seeds the Phase 3 arc). */
export function rivalWelcomeBeat(ctx: StoryContext): StoryBeat {
  const meta = REGION_META[ctx.region]
  return {
    id: 'rival:welcome',
    presentation: 'feed',
    feed: {
      authorId: 'rival-dt',
      headline: `¿Otro club en ${meta.label}? Bienvenido al barrio, ${ctx.clubName}. Veremos cuánto aguantas.`,
    },
  }
}

/** All beats seeded at the start of a new game, in display order. */
export function openingStoryBeats(ctx: StoryContext): StoryBeat[] {
  return [gameStartBeat(ctx), welcomePressBeat(ctx), rivalWelcomeBeat(ctx)]
}

// ---------------------------------------------------------------------------
// Event-driven beats — built from an engine event + context
// ---------------------------------------------------------------------------

/** First court finished → an excited fan post. Returns null for non-court jobs. */
export function firstCourtBeat(ctx: StoryContext): StoryBeat | null {
  const kind = (ctx.payload as { kind?: string } | undefined)?.kind
  if (kind !== 'court') return null
  return {
    id: 'firstCourt',
    presentation: 'feed',
    feed: {
      authorId: 'fan-pepe',
      headline: `¡Primera pista en marcha en ${ctx.clubName}! Huele a caucho nuevo. Reservo ya. 🎾`,
    },
  }
}

/** End of the first league season → a cinematic milestone reveal. */
export function firstSeasonBeat(ctx: StoryContext): StoryBeat {
  const meta = REGION_META[ctx.region]
  return {
    id: 'firstSeason',
    presentation: 'modal',
    modal: {
      kicker: 'Fin de temporada',
      title: 'Tu primera liga',
      body: `Cae el telón de tu primera temporada al frente de ${ctx.clubName}. Pase lo que pase en la clasificación, ${meta.label} ya conoce tu nombre. Esto no ha hecho más que empezar.`,
      heroImage: '/assets/broadcast/arena-dusk.webp',
      gradient: ['#141a2e', '#3b5bdb'],
      cta: 'Continuar',
    },
  }
}

// ---------------------------------------------------------------------------
// Recurring rival arc + board voice (season-scoped ids → fire every season)
// ---------------------------------------------------------------------------

/** The rival reacts (by feed) to the season's sporting outcome. */
export function rivalSeasonBeat(ctx: StoryContext): StoryBeat | null {
  const p = ctx.payload as Partial<SeasonOutcome> | undefined
  if (!p || typeof p.season !== 'number') return null
  if (p.title) {
    return feedBeat(`rival:title:s${p.season}`, 'rival-dt',
      `Vale, lo admito: ${ctx.clubName} es campeón. Disfrútalo, que la próxima temporada vuelvo a por todas.`)
  }
  if (p.promoted) {
    return feedBeat(`rival:promoted:s${p.season}`, 'rival-dt',
      `Así que ${ctx.clubName} asciende... subes de categoría para que te ganemos en una mejor. Nos vemos arriba.`)
  }
  if (p.relegated) {
    return feedBeat(`rival:relegated:s${p.season}`, 'rival-dt',
      `¿Descenso, ${ctx.clubName}? Lo veía venir de lejos. Nosotros, mientras, seguimos donde se compite.`)
  }
  return null
}

/** The board sets the season's objective → a weighty modal with personality. */
export function boardObjectiveBeat(ctx: StoryContext): StoryBeat | null {
  const p = ctx.payload as Partial<ObjectiveOutcome> | undefined
  if (!p || typeof p.season !== 'number' || !p.objectiveLabel) return null
  return {
    id: `board:objective:s${p.season}`,
    presentation: 'modal',
    modal: {
      kicker: 'La directiva',
      title: 'El encargo de la temporada',
      body: `La junta de ${ctx.clubName} ha hablado: este año esperan «${p.objectiveLabel}»${p.divisionName ? ` en ${p.divisionName}` : ''}. No habrá excusas — el palco estará observando cada jornada.`,
      gradient: ['#141a2e', '#3b5bdb'],
      cta: 'A por ello',
    },
  }
}

/**
 * The recurring "el clásico" derby — a choice presented via the EventPopup.
 * The dilemma + effects live in eventsConfig ('el_clasico'); this just routes
 * it. Season-scoped id so it recurs once per season. (The StorySystem decides
 * WHEN, since the matchday gate needs league state.)
 */
export function clasicoBeat(ctx: StoryContext): StoryBeat | null {
  const p = ctx.payload as { season?: number } | undefined
  if (!p || typeof p.season !== 'number') return null
  return { id: `clasico:s${p.season}`, presentation: 'choice', choice: { eventType: 'el_clasico' } }
}

/** The board's mood after the verdict, relayed by the press (feed). */
export function boardVerdictBeat(ctx: StoryContext, met: boolean): StoryBeat | null {
  const p = ctx.payload as Partial<ObjectiveOutcome> | undefined
  if (!p || typeof p.season !== 'number') return null
  const where = p.divisionName ? ` en ${p.divisionName}` : ''
  return met
    ? feedBeat(`board:met:s${p.season}`, 'padel-press',
        `La directiva de ${ctx.clubName} respira aliviada: objetivo de temporada cumplido${where}.`)
    : feedBeat(`board:failed:s${p.season}`, 'padel-press',
        `Ambiente tenso en el palco de ${ctx.clubName}: la junta no vio cumplido su objetivo esta temporada${where}.`)
}
